import {Tab, TabBar} from '../TabBar.js';
import {safeText} from "../../util/CommonUtils.js";
import DialogUtils from "../../../components/DialogUtils.js";

export default class ColumnPickerDialog {
    /** @type {HTMLDialogElement} */
    #dialog;

    constructor() {
        this.#dialog = null;
        this._onOkCallback = () => {/*noop*/}
    }

    /**
     * @param {{checkboxes: {name:string, checked: boolean, title: string|undefined}[], rowsFilter: string, customProp: boolean}} data
     * @returns {ColumnPickerDialog}
     */
    render(data) {
        if (this.#dialog)
            this.#dialog.remove();
        this.#dialog = this.#createDialog(data.rowsFilter);
        document.body.insertAdjacentElement('beforeend', this.#dialog);
        DialogUtils.setDefaultDialogListeners(this.#dialog);
        this._tabBar = new TabBar(this.#dialog.querySelector("#tabs"), [new Tab('Columns'), new Tab('Rows')]);
        this._tabBar.onTabClick((tabIndex) => {
            this._tabBar.setActiveTab(tabIndex);
            this._showTabContent(tabIndex);
        })

        let columnFilter = this.#dialog.querySelector('#column-filter');

        columnFilter.addEventListener('input', () => {
            this._onFilterChange(columnFilter.value.trim());
        })

        this._rowFilter = this.#dialog.querySelector('#row-filter');

        let cancelBtn = this.#dialog.querySelector('.btn-cancel');
        cancelBtn.addEventListener('click', () => {
            this.remove();
        })

        let okBtn = this.#dialog.querySelector('.btn-ok');
        okBtn.addEventListener('click', () => {
            this.#dialog.querySelector('.progress').style.display = 'block';
            okBtn.setAttribute('disabled', true);
            this._onApplyClick();
            this.#dialog.querySelector('.progress').style.display = null;
        });
        this.#dialog.addEventListener("cancel", this.remove.bind(this));
        this._fillColumnList(data.checkboxes);

        let globalCheckbox = this.#dialog.querySelector('#global-selector');
        globalCheckbox.addEventListener('click', (evt) => {
            evt.target.indeterminate = false;
            checkboxes.forEach(checkbox => {
                if (checkbox.getBoundingClientRect().width !== 0)
                    checkbox.checked = evt.target.checked
            })
        });

        let checkboxes = this.#dialog.querySelectorAll('.js-column-picker-dialog__col-checkbox');
        let total = checkboxes.length;
        checkboxes.forEach(checkbox => {
            checkbox.addEventListener('change', () => {
                let empty = this.#dialog.querySelectorAll('.js-column-picker-dialog__col-checkbox:not(:checked)').length;
                globalCheckbox.indeterminate = false;
                if (total === 0 || total === empty)
                    globalCheckbox.checked = false;
                else if (empty === 0 && total > 0)
                    globalCheckbox.checked = true;
                else
                    globalCheckbox.indeterminate = true;
            })
        });
        this.#dialog.showModal();
        columnFilter.focus();
        return this;
    }

    remove() {
        this.#dialog.remove();
    }

    onOk(cb){
        this._onOkCallback = cb;
    }

    setActiveTab(tabIndex){
        this._tabBar.setActiveTab(tabIndex)
        this._showTabContent(tabIndex);
    }

    /**
     * @param {string} message
     */
    showRowFilterError(message) {
        this.setActiveTab(1);
        this.#dialog.querySelector('.progress').style.display = 'none';
        this.#dialog.querySelectorAll('button').forEach(btn => {
            btn.removeAttribute('disabled');
        });
        this.#dialog.querySelectorAll('text-input').forEach((input) => {
            input.setDisabled(false)
        })
        let input = this.#dialog.querySelector(`#row-filter`);
        if (input) {
            input.errorMessage = message;
            input.focus();
        }
    }

    _showTabContent(tabIndex){
        switch (tabIndex) {
            case 0: {
                document.querySelector('#tab-2-content').style.display = 'none';
                document.querySelector('#tab-1-content').style.display = 'block';
                document.querySelector('#column-filter').focus();
                break;
            }
            case 1:{
                document.querySelector('#tab-1-content').style.display = 'none';
                document.querySelector('#tab-2-content').style.display = 'block';
                document.querySelector('#row-filter').focus();
            }
        }
    }

    _onApplyClick() {
        this.#dialog.querySelectorAll('text-input').forEach(el => {
            el.setDisabled(true)});
        this.#dialog.querySelectorAll('button').forEach(el => {
                el.setAttribute('disabled', true);
        });
        this._onOkCallback({columnNames: this._getSelectedColumns(), filterString: this._rowFilter.value.trim()})
        this.#dialog.querySelectorAll('text-input').forEach(el => {
            el.setDisabled(false)});
        this.#dialog.querySelectorAll('button').forEach(el => {
                el.removeAttribute('disabled');
        });
    }

    /**
     * @returns {string[]}
     * @private
     */
    _getSelectedColumns(){
        const columns = [];
        this.#dialog.querySelectorAll('.js-column-picker-dialog__col-checkbox:checked').forEach(input=>{
            columns.push(input.parentNode.parentNode.dataset.name);
        })
        return columns;
    }

    _onFilterChange(filter) {
        let res = '';
        // squash repeating asterisks into the one
        for (let i = 0; i < filter.length; i++) {
            const char = filter.charAt(i);
            if (char === "*" && i > 0 && res[res.length - 1] === "*")
                continue;
            res += filter[i];
        }
        let exp = new RegExp('^.*' + res.replace(/[-[\]{}()+?.,\\^$|#\s]/g, '\\$&').replace(/\*/g, '.*') + '.*$', 'i');
        this.#dialog.querySelectorAll('#column-list li').forEach((row, i) => {
            row.style.display = exp.test(row.dataset.name) ? 'block' : 'none';
        });
    }

    /**
     * @param {{name:string, checked:boolean, title: string|undefined, customProp: boolean}[]} checkboxes
     * @private
     */
    _fillColumnList(checkboxes) {
        this.#dialog.querySelector('#column-list').innerHTML =
            checkboxes.map(c => {
                const customMarker = c.customProp ? '<span class="column-picker-dialog__custom-marker" title="Custom user-defined property">*</span>': '';
                return `<li data-name="${safeText(c.name)}">
                                  <label class="column-picker-dialog__col-label" title="${safeText(c.title) || ''}">
                                    <input class="js-column-picker-dialog__col-checkbox" type="checkbox" ${c.checked ? 'checked' : ''}>${safeText(c.name)}
                                    ${customMarker}
                                  </label>
                                </li>`
            }).join("");
    }

    /**
     * @param {string} rowFilter
     * @returns {HTMLDialogElement}
     * @private
     */
    #createDialog(rowFilter) {
        const el = document.createElement("dialog");
        el.setAttribute('id', 'column-picker-dialog');
        el.innerHTML = ColumnPickerDialog.#htmlTemplate(rowFilter);
        return el;
    }

    /**
     * @param {string} rowFilter
     * @returns {string}
     */
    static #htmlTemplate(rowFilter = '') {
        return `
              <div class="progress">
                <div class="indeterminate"></div>
              </div>
              <div class="dialog-content">
                <div id="tabs"></div>
                <div id="tab-1-content">
                  <text-input id="column-filter" label="Find column by name"></text-input>
                  <input id="global-selector" type="checkbox">
                  <ul id="column-list"></ul>          
                </div>
                <div id="tab-2-content" style="display: none">
                  <div class="form-row form-row__input-w-icon">
                    <text-input id="row-filter" label="Filter (e.g. col['Area%'] > 50)" value="${safeText(rowFilter)}"></text-input>
                    <a target="_blank" href="https://elsci.io/docs/peaksel/viz/expressions.html">
                      <i id="input-icon" class="material-symbols-outlined action-icons__item action-icons__item--after-input">help</i>
                    </a>
                  </div>
                </div>
              </div>
              <div class="dialog-buttons-row">
                <button class="button button--text btn-cancel">Cancel</button>
                <button class="button button--contained btn-ok">Apply</button>
              </div>
           `
    }
}